import type { DomainSettings } from '@bcf-vanilla-ts-v1-api-access/ext-api/models/domain-settings';
/**
 * @type HreflangAlternateEntry
 * Represents a single hreflang entry in SEO metadata.
 * @property lang - Language code (e.g., "en", "pl", "x-default").
 * @property url - Full URL for the specific language version.
 */
export type HreflangAlternateEntry = {
  lang: string;
  url: string;
};

/**
 * @type BuildHreflangAlternatesParams
 * Input parameters for the hreflang alternates builder function.
 * @property url - Current page URL for which alternates are generated.
 * @property alternates - Optional list of available language versions with their base URLs.
 */
export type BuildHreflangAlternatesParams = {
  url: string;
  alternates?: Array<{
    lang: string;
    url: string;
  }>;
  domainSettings?: DomainSettings;
};

/**
 * Helper function (Higher-Order Function) that returns a function for mapping URL segments.
 * If a path segment is a language code (2 characters), it replaces it with the target hreflang code.
 *
 * @param hreflangLanguage - Target language code to appear in the URL.
 * @param allLangsWithXDefault - List of all known language codes that should be replaced.
 */
function mapToUpdatedSegment(hreflangLanguage: string, allLangsWithXDefault: string[]): (segment: string) => string {
  return (segment: string): string => {
    if (segment.length === 2 && allLangsWithXDefault.includes(segment)) {
      return hreflangLanguage;
    }

    return segment;
  };
}

/**
 * Builds a full alternate URL for a language by swapping only the language path segment.
 * Use this when callers start from the current page pathname and need per-language URLs
 * before passing them to buildHreflangAlternates.
 */
export function buildAlternateUrlForLang(
  pagePathname: string,
  lang: string,
  activeDomain: string,
  availableLangs: string[]
): string {
  const allLangsWithXDefault: string[] = ['x-default', ...availableLangs];
  const pathnameForLang: string = pagePathname
    .split('/')
    .map(mapToUpdatedSegment(lang, allLangsWithXDefault))
    .join('/');

  return new URL(pathnameForLang, `https://${activeDomain}`).toString();
}

/**
 * Main function generating a list of hreflang tags for SEO.
 * Hreflang informs Google which version of the page is intended for users from a specific region/language.
 *
 * Uses the pre-built alternate URLs as the source of truth for each language version.
 * Callers are responsible for providing fully localized pathnames when route segments differ per language.
 *
 * @param params - Object containing the current URL and list of alternates.
 * @returns Array of objects ready to be injected into <link rel="alternate"> tags.
 */
export function buildHreflangAlternates(
  { url, alternates, domainSettings }: BuildHreflangAlternatesParams,
  xDefaultLang: string = 'en'
): HreflangAlternateEntry[] {
  if (!alternates || alternates.length === 0) {
    return [];
  }

  const normalizeLang = (lang: string): string => lang.trim().toLowerCase().replace(/_/g, '-');

  const pageUrl: URL = new URL(url);
  const currentHostWithoutNumber: string = pageUrl.hostname.replace(/\d+(?=\.[^.]+$)/, '');

  const langToUrl: Map<string, string> = new Map<string, string>();
  for (const alternate of alternates) {
    const normalizedLang: string = normalizeLang(alternate.lang);
    if (!normalizedLang || normalizedLang === 'x-default' || normalizedLang.length !== 2) {
      continue;
    }

    if (!langToUrl.has(normalizedLang)) {
      langToUrl.set(normalizedLang, alternate.url);
    }
  }

  const availableLangs: string[] = Array.from(langToUrl.keys());
  const defaultLang: string = availableLangs.includes('en') ? xDefaultLang : (availableLangs[0] ?? 'en');
  const resultByHreflang: Map<string, string> = new Map<string, string>();

  const defaultUrl: string | undefined = langToUrl.get(defaultLang);
  if (defaultUrl) {
    // Preserve protocol, port, pathname and search (e.g. ?disciplineId=) from the default
    // alternate's own URL; only the hostname is normalized (strips numbered env suffixes).
    const defaultUrlWithNormalizedHost: URL = new URL(defaultUrl);
    defaultUrlWithNormalizedHost.hostname = currentHostWithoutNumber;
    resultByHreflang.set('x-default', defaultUrlWithNormalizedHost.toString());
  }

  for (const lang of availableLangs) {
    const alternateUrl: string | undefined = langToUrl.get(lang);
    if (alternateUrl) {
      resultByHreflang.set(lang, alternateUrl);
    }
  }

  // en-{lang} only when domainSettings has an entry for that language key (e.g. PL for pl) with activeDomain.
  for (const lang of availableLangs) {
    const baseUrl: string | undefined = langToUrl.get(lang);
    if (!baseUrl) {
      continue;
    }

    if (lang === 'en') {
      continue;
    }

    const activeDomain: string | undefined = domainSettings?.[lang.toUpperCase()]?.activeDomain;
    if (!activeDomain) {
      continue;
    }

    const regionalHreflang: string = `en-${lang}`;
    const enUrlWithRegionalHost: URL = new URL(langToUrl.get('en') ?? baseUrl);
    enUrlWithRegionalHost.hostname = activeDomain;
    resultByHreflang.set(regionalHreflang, enUrlWithRegionalHost.toString());
  }

  return Array.from(resultByHreflang.entries()).map(([lang, url]: [string, string]) => ({ lang, url }));
}
